/**
 * Auth API Client (cookie-based).
 *
 * The frontend used to talk directly to Cognito and stash access /
 * refresh tokens in localStorage. That made any same-origin XSS into
 * a one-line account takeover (`JSON.parse(localStorage.getItem('fias_auth'))`).
 * This file now delegates every auth operation to our own backend at
 * `/v1/auth/*`, which sets httpOnly Secure cookies that JavaScript can
 * never read. Tokens never enter JS storage of any kind.
 *
 * Other callers of this module did not need to change — the public
 * methods (signIn / signUp / signOut / getCurrentUser / etc.) keep the
 * same shapes; only the implementations switched from "POST to Cognito,
 * write to localStorage" to "POST to our backend, let it set cookies".
 *
 * Two methods hand a string credential back to callers:
 *
 * - `getWebSocketTicket()` — mints a single-use 30s DynamoDB ticket via
 *   `POST /v1/auth/ws-ticket`. WebSocket / SSE handshakes can't carry
 *   cross-origin cookies reliably, so the ticket is what we put on the
 *   `?ticket=` query param. Single-use means a leaked URL is useless
 *   after the first handshake completes.
 *
 * - `getPluginBridgeToken()` — returns the raw session ID token,
 *   intentionally and only for sandboxed plugin iframes (opaque origin,
 *   cannot use parent cookies). SECURITY DEBT: a leaked token here grants
 *   full account access for ~1h. Phase 2 of the WS-ticket security work
 *   replaces this with capability-scoped per-arche tokens. Cached in
 *   memory (5min TTL) and invalidated on logout.
 */

import { API_BASE } from './api';
import { setCsrfToken, attachCsrfHeader } from './csrf';
import { refreshAuthSession } from './auth-refresh';

// =============================================================================
// Dev mode
// =============================================================================
//
// VITE_DEV_MODE is honored only on hostnames that unambiguously identify a
// developer environment so a leaked build flag in staging/prod can never
// enable dev login. The backend pairs this with its own dev-mode gate
// (Cognito not configured + explicit opt-in), so even if the gate fails
// open here, the backend rejects dev tokens in any real env.
//
// Allowed hostnames:
//   - localhost / 127.0.0.1 (local WSL, Docker, native dev)
//   - *.app.github.dev      (GitHub Codespaces forwarded URLs)

const isDevHost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.endsWith('.app.github.dev'));

if (import.meta.env.VITE_DEV_MODE === 'true' && !isDevHost) {
  console.error(
    '[Auth] SECURITY: VITE_DEV_MODE is set but hostname is not a recognized dev host. ' +
      'DEV_MODE has been disabled. This should never happen in production builds.',
  );
}

const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true' && isDevHost;

const DEV_USER_STORAGE_KEY = 'fias_dev_user_index';

/**
 * Role assignments for dev users (mirrors the backend's
 * services/jwt-verifier.ts). Used purely so the dev-user picker UI can
 * preview which roles a given dev user holds — the source of truth at
 * runtime is the backend's `getDevUserRoles`.
 */
function getDevUserRoles(num: number): string[] {
  switch (num) {
    case 1:
      return ['user', 'admin', 'contributor', 'beta_tester', 'litcraft_admin'];
    case 2:
      return ['user', 'contributor', 'beta_tester'];
    case 3:
      return ['user', 'beta_tester'];
    case 4:
      return ['user', 'contributor'];
    default:
      return ['user'];
  }
}

const DEV_USERS: AuthUser[] = Array.from({ length: 10 }, (_, i) => ({
  userId: `dev-user-${String(i + 1).padStart(3, '0')}`,
  username: `devuser${i + 1}`,
  email: i === 0 ? 'dev@localhost' : `dev${i + 1}@localhost`,
  roles: getDevUserRoles(i + 1),
  emailVerified: true,
}));

function getSelectedDevUserIndex(): number {
  if (!DEV_MODE) return 0;
  try {
    const stored = localStorage.getItem(DEV_USER_STORAGE_KEY);
    if (stored !== null) {
      const index = parseInt(stored, 10);
      if (index >= 0 && index < DEV_USERS.length) return index;
    }
  } catch {
    // Ignore storage errors
  }
  return 0;
}

function setSelectedDevUserIndex(index: number): void {
  localStorage.setItem(DEV_USER_STORAGE_KEY, String(index));
}

// =============================================================================
// Public types (shape preserved across the migration)
// =============================================================================

export interface AuthUser {
  userId: string;
  username: string;
  email: string;
  roles: string[];
  emailVerified: boolean;
}

interface ApiErrorBody {
  error?: { code?: string; message?: string };
}

/**
 * In-memory "we have a session" flag. Several streaming hooks call
 * `getIdToken()` synchronously as a gate to decide whether to attempt
 * a WebSocket connection at all (the actual ticket used for the
 * handshake is fetched async via `getWebSocketTicket`). Under cookie
 * auth we can't read the session token from JS, so we track presence
 * here and return a stable non-null marker when set. The marker is
 * never sent over the wire — it's purely a falsy/truthy signal.
 */
const SESSION_PRESENT_MARKER = 'fias-cookie-session';
let sessionPresent = false;

// In-memory cache for the plugin bridge token. Plugin iframes call
// `getAuthToken` via the postMessage bridge, potentially per request,
// and the underlying session cookie lives ~1h — so a 5min cache is
// well within validity and removes repeated round-trips. Invalidated
// on logout via `markSessionInactive` so a freshly-logged-out plugin
// doesn't get its old token back.
const PLUGIN_BRIDGE_CACHE_TTL_MS = 5 * 60 * 1000;
let cachedBridgeToken: { token: string; expiresAt: number } | null = null;

function markSessionActive(): void {
  sessionPresent = true;
}

function markSessionInactive(): void {
  sessionPresent = false;
  cachedBridgeToken = null;
}

// =============================================================================
// Fetch wrapper
// =============================================================================

/**
 * Performs an auth-API request with cookies attached and the CSRF
 * token (if known) echoed in the X-CSRF-Token header. Throws an Error
 * with the user-facing message from the backend's `error.message` on
 * non-2xx responses.
 */
async function authFetch<T>(
  path: string,
  init: { method?: string; body?: Record<string, unknown> } = {},
): Promise<T> {
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  attachCsrfHeader(method, headers);

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });

  // Capture CSRF token issued by login / refresh / dev-login responses
  // so subsequent state-changing requests can include it in the header.
  if (response.ok) {
    const data: unknown = await response.json().catch(() => ({}));
    const body = data as { csrfToken?: string } & Record<string, unknown>;
    if (typeof body.csrfToken === 'string') {
      setCsrfToken(body.csrfToken);
    }
    return body as T;
  }

  let message = response.statusText;
  try {
    const body = (await response.json()) as ApiErrorBody;
    if (body.error?.message) message = body.error.message;
  } catch {
    // Non-JSON error body — fall back to statusText
  }
  throw new Error(message);
}

// =============================================================================
// Auth API
// =============================================================================

class AuthApiClient {
  // -------------------------------------------------------------------------
  // Sign-up
  // -------------------------------------------------------------------------

  async signUp(
    email: string,
    password: string,
    inviteCode: string,
    username: string,
  ): Promise<{ requiresConfirmation: boolean }> {
    return authFetch<{ requiresConfirmation: boolean }>('/auth/signup', {
      method: 'POST',
      body: { email: email.toLowerCase(), password, inviteCode, username },
    });
  }

  async confirmSignUp(
    email: string,
    code: string,
    inviteCode?: string,
    username?: string,
  ): Promise<void> {
    await authFetch('/auth/confirm-signup', {
      method: 'POST',
      body: {
        email: email.toLowerCase(),
        code,
        ...(inviteCode ? { inviteCode } : {}),
        ...(username ? { username } : {}),
      },
    });
  }

  async resendConfirmationCode(email: string): Promise<void> {
    await authFetch('/auth/resend-confirmation', {
      method: 'POST',
      body: { email: email.toLowerCase() },
    });
  }

  // -------------------------------------------------------------------------
  // Sign-in / sign-out
  // -------------------------------------------------------------------------

  async signIn(email: string, password: string): Promise<{ user: AuthUser }> {
    if (DEV_MODE) {
      const devIndex = getSelectedDevUserIndex();
      console.warn(`[DEV_MODE] Bypassing Cognito — using ${DEV_USERS[devIndex].email}`);
      await authFetch('/auth/dev-login', {
        method: 'POST',
        body: { devUserIndex: devIndex + 1 },
      });
      markSessionActive();
      return { user: DEV_USERS[devIndex] };
    }

    await authFetch('/auth/login', {
      method: 'POST',
      body: { email: email.toLowerCase(), password },
    });
    markSessionActive();
    // The cookie is set by the login response. Hydrate the user
    // profile via /auth/me so callers get back the same shape they
    // used to receive from the Cognito ID-token decode.
    const me = await this.getCurrentUser();
    if (!me) {
      throw new Error('Login succeeded but /auth/me returned no user');
    }
    return { user: me };
  }

  async signOut(): Promise<void> {
    try {
      await authFetch('/auth/logout', { method: 'POST' });
    } catch {
      // Logout is best-effort — cookies may be cleared by the server
      // even if the response shape was unexpected.
    }
    setCsrfToken(null);
    markSessionInactive();
  }

  // -------------------------------------------------------------------------
  // Session lookup
  // -------------------------------------------------------------------------

  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const result = await authFetch<{ user: AuthUser }>('/auth/me');
      markSessionActive();
      return result.user;
    } catch {
      // 401 (no session) or any transient error → treat as logged out.
      markSessionInactive();
      return null;
    }
  }

  /**
   * Refreshes the session cookie by calling /auth/refresh. The backend
   * uses the (httpOnly) refresh cookie to obtain a new access token and
   * sets a fresh CSRF cookie. Returns true on success, false if the
   * refresh failed and the user must log in again.
   *
   * Delegates to the shared `refreshAuthSession` helper so this call
   * coalesces with any concurrent refreshes triggered by the api.ts
   * 401 interceptor or the proactive AuthContext timer — the backend
   * never sees a thundering herd of refresh requests.
   */
  async refreshSession(): Promise<boolean> {
    return refreshAuthSession();
  }

  // -------------------------------------------------------------------------
  // Password reset / change
  // -------------------------------------------------------------------------

  async forgotPassword(email: string): Promise<void> {
    await authFetch('/auth/forgot-password', {
      method: 'POST',
      body: { email: email.toLowerCase() },
    });
  }

  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    await authFetch('/auth/confirm-forgot-password', {
      method: 'POST',
      body: { email: email.toLowerCase(), code, newPassword },
    });
  }

  /**
   * Change the password for the currently signed-in user. Backend
   * revokes all sessions (calls Cognito GlobalSignOut) and clears
   * cookies, so the caller is logged out on success.
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await authFetch('/auth/change-password', {
      method: 'POST',
      body: { oldPassword, newPassword },
    });
    setCsrfToken(null);
  }

  // -------------------------------------------------------------------------
  // Credentials for WebSocket handshakes and plugin iframe bridging
  //
  // Two separate concerns, two separate methods:
  //
  // - `getWebSocketTicket()` mints a single-use, 30s, DynamoDB-backed
  //   ticket via `POST /auth/ws-ticket`. Use as `?ticket=` query param
  //   on the WebSocket URL. Cannot be replayed; cannot be used against
  //   the REST API. This is the secure path — prefer it.
  //
  // - `getPluginBridgeToken()` returns the raw session ID token, used
  //   ONLY by sandboxed plugin iframes that have an opaque origin and
  //   cannot use the parent's cookies. SECURITY DEBT: a leaked token
  //   here grants full account access for the cookie's remaining
  //   lifetime (~1h). Phase 2 of this work replaces it with
  //   capability-scoped per-arche tokens. Cached in memory to avoid
  //   repeated round-trips from chatty plugins.
  //
  // The deprecated `getValidIdToken()` / `getValidAccessToken()` are
  // shims preserved for the brief migration window only. They will be
  // removed in commit 2.
  // -------------------------------------------------------------------------

  async getWebSocketTicket(): Promise<string | null> {
    try {
      const result = await authFetch<{ ticket: string }>('/auth/ws-ticket', { method: 'POST' });
      return result.ticket;
    } catch {
      // Session cookie may have just expired. Try one refresh + retry
      // so the streaming hooks recover transparently when the user is
      // mid-action; only fall through to null if the session is
      // truly gone (the streaming hook then surfaces UNAUTHORIZED and
      // triggers the SessionExpiredListener modal).
      const refreshed = await refreshAuthSession();
      if (!refreshed) return null;
      try {
        const result = await authFetch<{ ticket: string }>('/auth/ws-ticket', { method: 'POST' });
        return result.ticket;
      } catch {
        return null;
      }
    }
  }

  async getPluginBridgeToken(): Promise<string | null> {
    const now = Date.now();
    if (cachedBridgeToken && cachedBridgeToken.expiresAt > now) {
      return cachedBridgeToken.token;
    }
    try {
      const result = await authFetch<{ token: string }>('/auth/plugin-bridge-token');
      cachedBridgeToken = {
        token: result.token,
        expiresAt: now + PLUGIN_BRIDGE_CACHE_TTL_MS,
      };
      return result.token;
    } catch {
      return null;
    }
  }

  /**
   * Synchronous "do we have a session?" probe. Returns a stable
   * non-null marker string when the user is logged in, or null
   * otherwise. Used by streaming/WebSocket hooks as a render-time
   * gate; they call `getWebSocketTicket()` async to obtain the actual
   * ticket used in the WS handshake. The marker is never sent on the
   * wire — it's purely a truthy/falsy signal.
   */
  getIdToken(): string | null {
    return sessionPresent ? SESSION_PRESENT_MARKER : null;
  }

  getAccessToken(): string | null {
    return this.getIdToken();
  }

  // -------------------------------------------------------------------------
  // Dev mode helpers
  // -------------------------------------------------------------------------

  isDevMode(): boolean {
    return DEV_MODE;
  }

  getDevUsers(): AuthUser[] {
    return DEV_USERS;
  }

  /**
   * Switches to a different dev user. Re-runs /auth/dev-login so the
   * server-set cookies are updated to match the new dev user.
   */
  async switchDevUser(index: number): Promise<AuthUser | null> {
    if (!DEV_MODE) return null;
    if (index < 0 || index >= DEV_USERS.length) return null;
    setSelectedDevUserIndex(index);
    try {
      await authFetch('/auth/dev-login', {
        method: 'POST',
        body: { devUserIndex: index + 1 },
      });
      markSessionActive();
    } catch (err) {
      console.warn('[DEV_MODE] dev-login failed', err);
      return null;
    }
    return DEV_USERS[index];
  }
}

export const authApi = new AuthApiClient();
export default authApi;
